import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { Client } from '@amityco/ts-sdk';

import Messages from "./components/Messages";
import Chat from "./components/Chat";
import NewChat from "./components/NewChat";
import Appointments from "./components/Appointments";

import "./styles.scss";
import { useAppSelector } from "@store";
import { IChannel, listUserChannels } from "src/services/amity";
import { AuthContext } from "src/providers/AuthProvider";
import { config } from "src/config";

const ChatMessages = () => {
  const [loadingChats, setLoadingChats] = useState<boolean>(false);
  const [amityChannels, setAmityChannels] = useState<IChannel[]>([]);
  const [channel, setChannel] = useState<IChannel | null>(null);
  const { userIdAmity } = useAppSelector(state => state.login)
  const { allUsers, profileId, isConnected } = useContext(AuthContext);
  const isFetchingRef = useRef(false);
  const hasInitialLoadRef = useRef(false);

  const filterChannels = (channels: IChannel[]) => {
    const filtered = channels
      ?.filter((channel) => channel.displayName)
      ?.filter((channel) => {
        return true;
      })
      ?.filter((channel, index, self) => index === self.findIndex((c) => c.id === channel.id));

    // Canais de pacotes: exibir todos (ordenados por lastActivity desc), não apenas um.
    // Assim o canal que recebeu a mensagem do backend aparece na lista e as mensagens são exibidas.
    const packageChannels = filtered
      .filter(c => c.isPackageChannel === true)
      .sort((a, b) => b.lastActivity.getTime() - a.lastActivity.getTime());
    const otherChannels = filtered.filter(c => c.isPackageChannel !== true);

    return [...otherChannels, ...packageChannels];
  };

  const getAllChannels = useCallback(async (id: string, force = false) => {
    if (isFetchingRef.current && !force) return;
    isFetchingRef.current = true;
    setLoadingChats(true);
    try {
      await listUserChannels(
        id,
        (channels) => setAmityChannels(filterChannels(channels)),
        () => {
          setLoadingChats(false);
          isFetchingRef.current = false;
        },
      );
    } catch (error) {
      console.error('Amity:', error);
      setLoadingChats(false);
      isFetchingRef.current = false;
    }
  }, []);

  const [currentView, setCurrentView] = useState<"list" | "chat" | "newChat" | "appointments">("list");

  const userId = profileId || userIdAmity;

  // Carregamento inicial - apenas uma vez quando conectado
  useEffect(() => {
    if (isConnected && userId && !hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      getAllChannels(userId as string);
    }
  }, [isConnected, userId, getAllChannels]);

  // Atualizar lista de canais quando volta para a lista (para atualizar lastActivity)
  useEffect(() => {
    if (currentView === "list" && isConnected && userId && hasInitialLoadRef.current) {
      getAllChannels(userId as string);
    }
  }, [currentView, isConnected, userId, getAllChannels]);

  // Atualizar lista de canais periodicamente para manter lastActivity atualizado
  useEffect(() => {
    if (!isConnected || !userId || !config.enablePolling) return;

    const intervalId = setInterval(() => {
      if (!isFetchingRef.current) {
        getAllChannels(userId as string);
      }
    }, 60000); // Aumentado para 60 segundos

    return () => clearInterval(intervalId);
  }, [isConnected, userId, getAllChannels]);

  const handleOpenMessages = (channel: IChannel) => {
    setChannel(channel);
    setCurrentView("chat");
  }

  const handleBackToList = () => {
    setChannel(null);
    setCurrentView("list");
  }

  const handleNewChat = () => setCurrentView("newChat");

  const handleOpenScheduling = () => setCurrentView("appointments");

  return (
    <div className="chat-sidebar">
      {currentView === "list" &&
        <Messages
          loadingChats={loadingChats}
          channels={amityChannels}
          handleNewChat={handleNewChat}
          handleOpenScheduling={handleOpenScheduling}
          handleOpenMessages={handleOpenMessages}
          handleUpdateChat={() => {
            if (userId) getAllChannels(userId as string, true)
          }}
        />}

      {currentView === 'chat' &&
        <Chat
          userId={userIdAmity || profileId}
          userIdAmity={userIdAmity}
          channel={channel}
          handleBackToList={handleBackToList}
        />}

      {currentView === "newChat" &&
        <NewChat
          handleBackToList={handleBackToList}
          userId={profileId}
          handleOpenMessages={handleOpenMessages}
          allUsers={allUsers}
          channels={amityChannels}
        />}

      {currentView === "appointments" &&
        <Appointments
          handleBackToList={handleBackToList}
        />}
    </div>
  );
};

export default ChatMessages;
